/// <amd-module name="Build/Admin/Agent/AgentSignalRHub" />
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";

import { HubNames, IAgentHubServer, IAgentHubClient, getDTProxyToRegister } from "DistributedTask/Common/SignalR/SignalRHubProxies";

import { AgentActionCreator } from "Build/Admin/Agent/Actions/AgentActionCreator";
import { SignalRHubBase, ISignalRHubOptionsBase } from "Build/Admin/Common/SignalRHubBase";

export interface IAgentSignalRHubOptions extends ISignalRHubOptionsBase {
    actionCreator: AgentActionCreator;
}

// We create a wrapper class around the hub implementation and make the actual hub a static member of the wrapper class
// to enforce the fact that there should only be one active hub on a particular view at any given time.
export class AgentSignalRHub {
    private static hub: AgentSignalRHubImpl | undefined;

    constructor(options: IAgentSignalRHubOptions) {
        if (!AgentSignalRHub.hub) {
            AgentSignalRHub.hub = new AgentSignalRHubImpl(options);
        }
    }

    public listen(poolId: number, agentId: number): void {
        if (AgentSignalRHub.hub) {
            AgentSignalRHub.hub.listen(poolId, agentId);
        }
    }

    public dispose(): void {
        if (AgentSignalRHub.hub) {
            AgentSignalRHub.hub.dispose();
            AgentSignalRHub.hub = undefined;
        }
    }
}

class AgentSignalRHubImpl extends SignalRHubBase<IAgentHubClient, IAgentHubServer, IAgentSignalRHubOptions> {
    private _poolId: number | undefined;
    private _agentId: number | undefined;

    constructor(options: IAgentSignalRHubOptions) {
        super(HubNames.agentHub, options);

        this.initializeProxy(getDTProxyToRegister, this._initializeClient);
        this.addListener(this);
    }

    public listen(poolId: number, agentId: number): void {
        this._poolId = poolId;
        this._agentId = agentId;

        this.listenTimer = window.setTimeout(
            () => {
                this.subscribePromise = this.start().then(() => {
                    const hub = this.hub;
                    if (hub && this._poolId && this._agentId) {
                        hub.server.subscribe(this._poolId, this._agentId);
                    }
                });
            },
            1000 // run on a brief timeout, to avoid quick switching
        );
    }

    public dispose(): void {
        this.clearTimer();

        const hub = this.hub;
        // If we never called .start() then we don't need to call super.dispose()
        if (hub && this.subscribePromise) {
            this.subscribePromise.then(() => {
                if (this._poolId && this._agentId) {
                    hub.server.unsubscribe(this._poolId, this._agentId)
                        .then(() => super.dispose(), () => super.dispose());
                }
            });
        }
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IAgentHubClient, IAgentHubServer>) => {
        proxy.client.notifyAgentRequestUpdated = (poolId: number, agentId: number, requestId: number) => {
            this.options.actionCreator.getRequestData(poolId, agentId, requestId);
        };
    }
}
